import { observable } from "@legendapp/state";
import { getPerformanceReport } from "@/apis/clients/reportingClient";
import {
  createRequestGuard,
  reportScopeKey,
  toErrorMessage,
} from "@/stores/storeHelpers";
import type { PerformanceReportRow, ReportScope } from "@/types/reporting";

interface ReportingState {
  rows: PerformanceReportRow[];
  isLoading: boolean;
  error: string | null;
  loadedForScopeKey: string | null;
}

const defaultValues: ReportingState = {
  rows: [],
  isLoading: true,
  error: null,
  loadedForScopeKey: null,
};

export const ReportingStore$ = observable(defaultValues);

const guard = createRequestGuard();

export const fetchPerformanceReport = async (scope: ReportScope) => {
  const scopeKey = reportScopeKey(scope);
  const result = guard.check(scopeKey, ReportingStore$.loadedForScopeKey.get());
  if (result.outcome === "cached") {
    ReportingStore$.assign({ isLoading: false, error: null });
    return;
  }
  if (result.outcome === "in-flight") return;
  const { requestId } = result;
  ReportingStore$.assign({ isLoading: true, error: null });

  try {
    const response = await getPerformanceReport({
      ...scope,
      dimensions: ["agg_timestamp"],
      metrics: [
        "clicks",
        "ctr",
        "impressions",
        "total_conversions",
        "total_spend",
        "total_spend_cpa",
        "video_complete",
        "video_start",
      ],
    });
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    ReportingStore$.assign({
      rows: response.results,
      loadedForScopeKey: scopeKey,
    });
  } catch (error) {
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    ReportingStore$.error.set(toErrorMessage(error));
  } finally {
    if (!guard.isStale(requestId)) {
      ReportingStore$.isLoading.set(false);
    }
  }
};

export const clearPerformanceReport = () => {
  guard.invalidate();
  ReportingStore$.assign({
    rows: [],
    isLoading: false,
    loadedForScopeKey: null,
    error: null,
  });
};
